/**
 * Deal-Specific Actions
 * Actions defined specifically for the deals entity
 * Strongly typed to Deal row type
 * Uses shared builders to eliminate duplication
 *
 * Cross-entity actions (Call, Send Email, Send Text, Schedule Meeting,
 * Create Task, Make Ticket, Research Company, Find Decision Makers)
 * route through the Action Resolver — for deals, contact-targeted actions
 * ALWAYS walk deal → company → contact per CROSS_ENTITY_ACTION_RESOLVER.md.
 * Manifests + walker live in `lib/actions/{manifests,relationships}/`.
 */

import type { Deals } from "@/src/gql/graphql";
import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import {
  universalDeleteAction,
  universalSendEmailAction,
  universalLogActivityAction,
  universalAskMeridianAction,
  universalViewDetailsAction,
  universalConfigureAutomatedTasksAction,
  universalCreateTaskAction,
  universalMakeTicketAction,
  universalScheduleMeetingAction,
} from "@/lib/actions/definitions/universal-actions";
import {
  createStatusAction,
  createCallAction,
  createResearchCompanyAction,
  createFindDecisionMakersAction,
} from "@/lib/actions/definitions/shared";
import { createSendTextMessageAction } from "@/lib/actions/definitions/shared/contact-actions";
import { ROLE_COMBINATIONS } from "@/lib/actions/types/action-properties";
import {
  createSendToSlackAction,
  createSendToSalesChannelAction,
  createOpenSlackChannelAction,
} from "@/lib/actions/definitions/shared/slack-actions";

/**
 * Complete deal actions array
 * Combines universal and deal-specific actions
 */
export const dealDetailActions: DetailActionStrict<Deals>[] = [
  // AI ASSISTANT ACTIONS
  universalAskMeridianAction<Deals>(),

  // PRIMARY ACTIONS (Header)
  createStatusAction<Deals>(),
  // NOTE: no Edit action — the detail page edits every field inline (click a
  // field on the Details tab). The old `universalEditAction` opened an unmounted
  // "edit" modal (a silent no-op), so it's removed as redundant. Tickets/tasks
  // keep theirs because UniversalDetailActions repurposes it into the inline-edit
  // lock/unlock toggle (EditToggleButton) for those two entities only.
  // Cross-entity (walks deal → company → contact via Action Resolver):
  universalSendEmailAction<Deals>(),
  createCallAction<Deals>(),
  // Available to salespeople (employees + admins) on the deal card; excludes
  // client-portal users. Walks deal → company → contact for the recipient.
  createSendTextMessageAction<Deals>({
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  }),
  // Template-prefilled text (opens the send-text modal with a deal template +
  // contact picker) — complements the resolver-backed "Send text" above.
  {
    id: "send-text-template",
    label: "Text from template",
    icon: "message-square",
    variant: "outline",
    action: "sendTextTemplate",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: false,
    tooltip: "Send a templated text message — pick the contact and a ready-made message.",
  },
  universalScheduleMeetingAction<Deals>(),
  universalLogActivityAction<Deals>(),

  // TABLE-ONLY ACTIONS
  universalViewDetailsAction<Deals>("viewDetails"),

  // SECONDARY ACTIONS (Menu)
  universalCreateTaskAction<Deals>(),
  universalMakeTicketAction<Deals>(),
  // deal → company (auto, one hop):
  createResearchCompanyAction<Deals>(),
  createFindDecisionMakersAction<Deals>(),
  createSendToSlackAction<Deals>(),
  createSendToSalesChannelAction<Deals>(),
  createOpenSlackChannelAction<Deals>(),
  universalConfigureAutomatedTasksAction<Deals>(),
  {
    id: "assign-deal-to-me",
    label: "Assign to me",
    icon: "user",
    variant: "outline",
    action: "assignDealToMe",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: true,
    tooltip: "Add yourself to this deal's owners.",
  },
  {
    id: "request-quote",
    label: "Request quote",
    icon: "file-text",
    variant: "outline",
    action: "requestQuote",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: false,
    tooltip:
      "Send a quote request to the technical team — creates a task with this deal's full context, due in 2 days.",
  },
  {
    id: "make-project-channel",
    label: "Make project channel",
    icon: "message-square",
    variant: "outline",
    action: "makeProjectChannel",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: false,
    tooltip:
      "Spin up a dedicated Slack project channel for this deal — the customer chats with the project team from their Hub portal.",
  },

  // FOLLOW-UP ACTIONS
  {
    id: "mark-followed-up",
    label: "Mark Followed Up",
    icon: "check",
    variant: "outline",
    action: "markFollowedUpThisWeek",
    allowedRoles: ["wtc_admin"],
    bulkable: true,
    visible: (deal: Deals) => {
      const { hasBeenFollowedUpThisWeek } = require("@/lib/utils/follow-up-tagging");
      return !hasBeenFollowedUpThisWeek((deal as any).tags);
    },
  },
  {
    id: "unmark-followed-up",
    label: "Unmark Followed Up",
    icon: "x",
    variant: "outline",
    action: "unmarkFollowedUpThisWeek",
    allowedRoles: ["wtc_admin"],
    bulkable: true,
    visible: (deal: Deals) => {
      const { hasBeenFollowedUpThisWeek } = require("@/lib/utils/follow-up-tagging");
      return hasBeenFollowedUpThisWeek((deal as any).tags);
    },
  },

  // SNOOZE ACTIONS — silence the weekly follow-up reminder for chosen weeks.
  {
    id: "snooze-deal",
    label: "Snooze reminders",
    icon: "clock",
    variant: "outline",
    action: "snoozeDeal",
    allowedRoles: ["wtc_admin"],
    bulkable: false,
    tooltip: "Pick weeks to stop follow-up reminders for this deal.",
  },
  {
    id: "unsnooze-deal",
    label: "Clear snooze",
    icon: "x",
    variant: "outline",
    action: "unsnoozeDeal",
    allowedRoles: ["wtc_admin"],
    bulkable: true,
    visible: (deal: Deals) => {
      const { hasActiveSnooze } = require("@/lib/utils/task/snooze-tagging");
      return hasActiveSnooze((deal as any).tags);
    },
  },

  // SCHEDULED FOLLOW-UP — set a specific week to follow up (visible to team).
  {
    id: "set-followup-date",
    label: "Set follow-up date",
    icon: "calendar",
    variant: "outline",
    action: "setFollowUpDate",
    allowedRoles: ["wtc_admin"],
    bulkable: false,
    tooltip: "Schedule this deal to follow up on a specific week.",
  },
  {
    id: "clear-followup-date",
    label: "Clear follow-up date",
    icon: "x",
    variant: "outline",
    action: "clearFollowUpDate",
    allowedRoles: ["wtc_admin"],
    bulkable: true,
    visible: (deal: Deals) => {
      const { hasActiveScheduledFollowup } = require("@/lib/utils/task/scheduled-followup-tagging");
      return hasActiveScheduledFollowup((deal as any).tags);
    },
  },

  // STAGE UPDATE ACTIONS
  {
    id: "mark-paid",
    label: "Mark as Paid",
    icon: "dollar-sign",
    variant: "success",
    action: "markDealPaid",
    allowedRoles: ["wtc_admin"],
    bulkable: true,
    visible: (deal: Deals) => deal.stage !== "paid",
  },
  {
    id: "mark-in-progress",
    label: "Mark as In-Progress",
    icon: "clock",
    variant: "outline",
    action: "markDealInProgress",
    allowedRoles: ["wtc_admin"],
    bulkable: true,
    visible: (deal: Deals) => deal.stage !== "in_progress",
  },

  universalDeleteAction<Deals>(),
];
